// Device identity utilities for Sally Lottery System device-lock.
// Produces: a persistent device_uuid + a SHA-256 fingerprint hash + metadata.

const UUID_KEY = "sally_device_uuid";

function generateUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // RFC4122 v4 fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getDeviceUuid() {
  try {
    let id = localStorage.getItem(UUID_KEY);
    if (!id) {
      id = generateUuid();
      localStorage.setItem(UUID_KEY, id);
    }
    return id;
  } catch {
    // Private mode or storage blocked: fall back to session-scoped id.
    if (!window.__sally_device_uuid) {
      window.__sally_device_uuid = generateUuid();
    }
    return window.__sally_device_uuid;
  }
}

function detectOs(ua) {
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad|ipod/i.test(ua)) return "iOS";
  if (/mac os|macintosh/i.test(ua)) return "macOS";
  if (/windows/i.test(ua)) return "Windows";
  if (/linux/i.test(ua)) return "Linux";
  return "Otro";
}

function detectBrowser(ua) {
  if (/edg\//i.test(ua)) return "Edge";
  if (/chrome\//i.test(ua) && !/chromium/i.test(ua)) return "Chrome";
  if (/safari\//i.test(ua) && !/chrome/i.test(ua)) return "Safari";
  if (/firefox\//i.test(ua)) return "Firefox";
  return "Navegador";
}

function detectType(ua) {
  if (/ipad|tablet/i.test(ua)) return "tablet";
  if (/iphone|android|mobile/i.test(ua)) return "mobile";
  return "desktop";
}

export function getDeviceMetadata() {
  const ua = navigator.userAgent || "";
  const os = detectOs(ua);
  const browser = detectBrowser(ua);
  const type = detectType(ua);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const lang = navigator.language || "";
  const screen = `${window.screen?.width || 0}x${window.screen?.height || 0}`;
  return {
    device_name: `${os} · ${browser}`,
    device_type: type,
    browser_name: browser,
    os_name: os,
    screen,
    timezone: tz,
    language: lang,
  };
}

async function sha256(str) {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const buf = new TextEncoder().encode(str);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // Non-cryptographic fallback (still deterministic). SubtleCrypto is available in all supported browsers.
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
  return ("0".repeat(16) + (h >>> 0).toString(16)).slice(-16);
}

export async function buildFingerprintHash(meta) {
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const payload = [
    ua,
    platform,
    meta.os_name,
    meta.browser_name,
    meta.device_type,
    meta.screen,
    meta.language,
    meta.timezone,
    navigator.hardwareConcurrency || "",
    (navigator.vendor || ""),
  ].join("|");
  return await sha256(payload);
}

export async function getDeviceIdentity() {
  const meta = getDeviceMetadata();
  const device_uuid = getDeviceUuid();
  const fingerprint_hash = await buildFingerprintHash(meta);
  return { device_uuid, fingerprint_hash, device_meta: meta };
}
